import React, {useEffect, useState} from "react"
import {useAccessToken} from "../../services/auth"
import {range} from "lodash"
import Skeleton from "react-loading-skeleton"
import {usePaging} from "../../hooks/usePaging"
import {find, AuthorizationStatus} from "../../resources/authorization"
import {DataTableActionHeader, DataTableCard} from "../DataTable/DataTable"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {Filter} from "../Filter/Filter"
import {useQueryState} from "use-location-state"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {CurrencyRangeFilter} from "../Filter/CurrencyRangeFilter"
import {Cents} from "../../resources/transaction"
import SearchBox from "../SearchBox/SearchBox"
import {getAccountCards} from "../../resources/card"
import {ok} from "neverthrow"
import {
	AccountAuthorizationsColumns,
	AllowedAccountAuthorizationsColumns,
	buildAuthorizationRowCells,
} from "./accountAuthorizationsTableCells"
import {AuthorizationsTable} from "./AuthorizationsTableView"

export {AccountAuthorizationsColumns, buildAuthorizationRowCells, AuthorizationsTable}
export type {AllowedAccountAuthorizationsColumns}

function Pending() {
	return (
		<div className="table-container">
			<table className="table">
				<thead>
					<tr>
						<th>Id</th>
						<th>Card Last 4 Digits</th>
						<th>Amount</th>
						<th>Merchant Name</th>
						<th>Status</th>
						<th>Reason</th>
						<th>Created At</th>
					</tr>
				</thead>
				<tbody>
					{range(5).map((i) => (
						<tr key={i}>
							<td>
								<Skeleton />
							</td>
							<td>
								<Skeleton />
							</td>
							<td>
								<Skeleton />
							</td>
							<td>
								<Skeleton />
							</td>
							<td>
								<Skeleton />
							</td>
							<td>
								<Skeleton />
							</td>
							<td>
								<Skeleton />
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	)
}

export function AccountAuthorizations({
	accountId,
	cardId,
	limit = 10,
	isUsingPaging = true,
	token,
	refreshPage,
	includedColumns,
	enableStatusFilter = true,
	enableAmountFilter = false,
	enableLast4DigitsFilter = false,
}: {
	accountId: string
	cardId?: string
	limit?: number
	isUsingPaging?: boolean
	token?: string
	refreshPage: () => void
	includedColumns: Array<AllowedAccountAuthorizationsColumns>
	enableStatusFilter?: boolean
	enableAmountFilter?: boolean
	enableLast4DigitsFilter?: boolean
}) {
	const [refreshToken] = useRefreshToken()
	const accessToken = token ?? useAccessToken()
	const prefix = "auth"
	const statusesOptions = new Map(Object.values(AuthorizationStatus).map((v) => [v, v]))
	const [statuses, setStatuses] = useQueryState<AuthorizationStatus[]>(`${prefix}-filter[statuses]`, [
		AuthorizationStatus.Authorized,
	])
	const [sortBy, setSortBy] = useQueryState(`${prefix}-sort`, "-createdAt")
	const [fromAmount, setFromAmount] = useQueryState<Cents | "">(`${prefix}-filter[fromAmount]`, "")
	const [toAmount, setToAmount] = useQueryState<Cents | "">(`${prefix}-filter[toAmount]`, "")
	const [last4Digits, setLast4Digits] = useQueryState(`${prefix}-filter[last4Digits]`, "")
	const [filterCardId, setFilterCardId] = useState<string | undefined>(cardId)
	const NO_RESULTS_SENTINEL = "NO_CARD_FOUND"

	// If cardId prop is provided, use it directly. Otherwise, fetch cardId when last4Digits changes
	useEffect(() => {
		if (cardId) {
			setFilterCardId(cardId)
			return
		}

		async function fetchCardId() {
			if (last4Digits && last4Digits.length === 4) {
				try {
					const result = await getAccountCards(accessToken, 0, 1, accountId, undefined, last4Digits)
					result.match(
						(response) => {
							if (response.data && response.data.length > 0) {
								setFilterCardId(response.data[0].id)
							} else {
								// No card found - use sentinel value to show empty results
								setFilterCardId(NO_RESULTS_SENTINEL)
							}
						},
						() => setFilterCardId(NO_RESULTS_SENTINEL)
					)
				} catch (error) {
					setFilterCardId(NO_RESULTS_SENTINEL)
				}
			} else {
				setFilterCardId(undefined)
			}
		}
		fetchCardId()
	}, [last4Digits, accountId, accessToken, cardId])

	function toggleSort() {
		setSortBy(sortBy === "-createdAt" ? "createdAt" : "-createdAt")
		reset(limit)
	}

	function onSearchLast4Digits(search: string) {
		setLast4Digits(search)
		reset(limit)
	}

	function getAuthorizations(offset: number, limit: number) {
		// If filterCardId is the sentinel value, return empty array immediately
		if (filterCardId === NO_RESULTS_SENTINEL) {
			return Promise.resolve(ok({data: [], included: [], meta: undefined}))
		}
		return find(accessToken, offset, limit, accountId, statuses, sortBy, fromAmount, toAmount, filterCardId)
	}

	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		getAuthorizations,
		(x) => x.data.length,
		[statuses.join(","), refreshToken, sortBy, fromAmount, toAmount, filterCardId],
		`${prefix}-`,
		true
	)

	const statusFilter = (
		<Filter
			statuses={statuses}
			setStatuses={setStatuses}
			onFilterFunc={() => reset(limit)}
			options={statusesOptions}
			title="Status"
		/>
	)

	const amountFilter = (
		<CurrencyRangeFilter
			placeholder={"Amount"}
			presets={[]}
			from={fromAmount}
			setFrom={(value) => {
				setFromAmount(value)
				reset(limit)
			}}
			to={toAmount}
			setTo={(value) => {
				setToAmount(value)
				reset(limit)
			}}
		/>
	)

	const [last4DigitsSearchBox, setIsSearchLoading] = SearchBox(
		last4Digits,
		"Card Last 4 Digits",
		500,
		onSearchLast4Digits,
		true,
		4,
		4,
		"^\\d{4}$"
	)

	useEffect(() => {
		result.match(
			() => null,
			() => setIsSearchLoading(false),
			() => setIsSearchLoading(false)
		)
	}, [result])

	return (
		<DataTableCard className={"account-authorizations-card"}>
			<DataTableActionHeader
				title={"Authorizations"}
				filters={[
					...(enableStatusFilter ? [statusFilter] : []),
					...(enableAmountFilter ? [amountFilter] : []),
					...(enableLast4DigitsFilter ? [last4DigitsSearchBox] : []),
				]}
			/>
			<AsyncResultComponent asyncResult={result} pendingComponent={<Pending />}>
				{({value: authorizations}) => {
					return (
						<AuthorizationsTable
							authorizations={authorizations.data}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							isUsingPaging={isUsingPaging}
							refreshPage={refreshPage}
							includedColumns={includedColumns}
							sortFunction={toggleSort}
							sortBy={sortBy}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
